import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Hash, ExternalLink, ChevronLeft, Wallet } from "lucide-react";
import CardBrandIcon from "@/components/CardBrandIcon";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback } from "react";
import tick from '@/assets/pos/tick.svg';
import { usePosWebSocket } from "@/hooks/pos/use-pos-ws-adapter";
import { apiFetch } from "@/utils";
import { Transaction } from "@/constants";
import { useUser } from "@/hooks/use-user";
import { useToast } from "@/hooks/use-toast";
import TransactionDetailDialog from "@/components/TransactionDetailDialog";

interface OrderConfirmationProps {
	order: any;
	onBackToMenu: () => void;
}

export default function OrderConfirmation({ order: initialOrder, onBackToMenu }: OrderConfirmationProps) {
	console.log("[OrderConfirmation] Component rendered with initialOrder:", initialOrder?.id, initialOrder?.orderNumber);

	const queryClient = useQueryClient();
	const { uid } = useUser();
	const { toast } = useToast();
	const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
	const [isLoadingTransaction, setIsLoadingTransaction] = useState(false);

	// Use WebSocket for real-time order updates
	const { lastMessage } = usePosWebSocket({
		channels: ["orders"],
		enabled: !!initialOrder.id,
	});

	// Clear any stale cache for this order on mount
	useEffect(() => {
		console.log("[OrderConfirmation] Clearing cache, initialOrder.id:", initialOrder?.id);
		queryClient.removeQueries({ queryKey: ["order-confirmation"] });
	}, [queryClient, initialOrder?.id]);

	// Refetch order when WebSocket receives an update for this order
	useEffect(() => {
		if (lastMessage?.type === "order_update") {
			const payload = lastMessage.payload as { orderId?: number };
			if (payload.orderId === initialOrder.id) {
				queryClient.invalidateQueries({ queryKey: ["order-confirmation", initialOrder.id] });
			}
		}
	}, [lastMessage, initialOrder.id, queryClient]);

	// Fetch order status (no polling, WebSocket triggers refetch)
	const { data: fetchedOrder } = useQuery({
		queryKey: ["order-confirmation", initialOrder.id],
		queryFn: async () => {
			console.log("[OrderConfirmation] Fetching order from API:", initialOrder.id);
			const res = await apiFetch(`/v1/pos/orders/${initialOrder.id}`, { withCredentials: true });
			if (!res.ok) throw new Error("Failed to fetch order");
			const data = await res.json();
			console.log("[OrderConfirmation] API returned:", data?.id, data?.orderNumber);
			return data;
		},
		enabled: !!initialOrder.id,
		staleTime: 0,
		gcTime: 0,
	});

	console.log("[OrderConfirmation] fetchedOrder:", fetchedOrder?.id, "initialOrder:", initialOrder?.id);

	// Always use initial order first, then switch to fetched data for status updates
	const order = fetchedOrder || initialOrder;
	console.log("[OrderConfirmation] Using order:", order?.id, order?.orderNumber);

	const isCard = order.paymentMethod === 'CARD';

	const formatAmount = (usdAmount: number) => {
		if (isCard) return `$${usdAmount.toFixed(2)}`;
		const rate = order.sechExchangeRate ? parseFloat(order.sechExchangeRate) : null;
		if (!rate) return `$${usdAmount.toFixed(2)}`;
		return `${(usdAmount / rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} SECH`;
	};

	const handleTransactionClick = useCallback(async (transactionId: number) => {
		if (!uid || isLoadingTransaction) return;
		setIsLoadingTransaction(true);
		try {
			const res = await apiFetch(`/v1/transactions/${uid}`, { withCredentials: true });
			if (!res.ok) throw new Error("Failed to fetch transactions");
			const json = await res.json();
			const transactions: Transaction[] = json?.data?.items ?? json;
			const match = transactions.find((tx) => tx.id === transactionId);
			if (match) {
				setSelectedTransaction(match);
			} else {
				toast({ title: "Not Found", description: "Transaction not found.", variant: "destructive" });
			}
		} catch {
			toast({ title: "Error", description: "Failed to load transaction details.", variant: "destructive" });
		} finally {
			setIsLoadingTransaction(false);
		}
	}, [uid, isLoadingTransaction, toast]);

	const getStatusColor = (status: string) => {
		switch (status) {
			case "pending":
				return "bg-yellow-900/30 text-yellow-400 border-yellow-700";
			case "preparing":
				return "bg-blue-900/30 text-blue-400 border-blue-700";
			case "ready":
				return "bg-green-900/30 text-green-400 border-green-700";
			case "delivered":
				return "bg-zinc-700/30 text-zinc-300 border-zinc-600";
			case "cancelled":
				return "bg-red-900/30 text-red-400 border-red-700";
			case "completed":
				return "bg-green-900/30 text-green-400 border-green-700";
			default:
				return "bg-zinc-700/30 text-zinc-300 border-zinc-600";
		}
	};

	const formatPrice = (amount: number) => {
		return Number(amount).toFixed(2);
	};

	const formatStatus = (status: string) => {
		return status.charAt(0).toUpperCase() + status.slice(1);
	};

	// Calculate original subtotal from items (before discount)
	const originalSubtotal = order.items?.reduce((sum: number, item: any) => {
		return sum + (parseFloat(item.unitPriceUsd) * item.quantity);
	}, 0) || 0;

	return (
		<div className="bg-[var(--pos-secondary-background-color)] z-50 fade-in max-w-5xl mx-auto min-h-screen relative">
			{/* Fixed Header */}
			<div className="fixed top-0 left-0 right-0 z-40 bg-[var(--pos-secondary-background-color)]">
				<div className="flex items-center gap-3 py-4 max-w-5xl mx-auto px-5">
					<button
						type="button"
						aria-label="Go back"
						onClick={onBackToMenu}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold text-[var(--pos-primary-text-color)] font-montserrat">Order Confirmed</h1>
				</div>
			</div>

			<div className="flex flex-col items-center bg-[var(--pos-secondary-background-color)] px-5 pb-[64px] pt-[63px] text-center">

				{/* Success Message */}
				<div className="flex flex-col items-center w-full bg-[var(--pos-primary-background-color)] pt-4 pb-4 rounded-lg mb-2 overflow-hidden">
					<img className="mb-6 h-18 w-32" src={tick} alt=""/>
					<h2 className="font-montserrat text-xl leading-[22px] font-semibold text-[var(--pos-primary-text-color)] mb-2 ">Order Confirmed!</h2>
					<p className="font-figtree text-[var(--pos-secondary-text-color)] leading-4 text-[13px] font-regular">
						Your items has been placed and is on it's way <br/> to being processed
					</p>
				</div>

				{/* Order Details Card */}
				<Card className="w-full p-2 rounded-lg border-0 mb-2 bg-[var(--pos-primary-background-color)]">
					<CardContent className="p-0">
						<div className="">
							{/* Order Number */}
							<div className="flex items-center justify-between pb-3 mb-3 border-b border-dashed border-[var(--pos-layout-border-color)]">
								<h3 className="font-montserrat text-[13px] leading-[18px] font-semibold text-[var(--pos-primary-text-color)]">Order ID: {order.orderNumber}</h3>
								<Badge className={`${getStatusColor(order.status)} border-0 font-figtree h-6 flex items-center justify-center rounded-lg text-sm leading-[18px] font-medium`}>
									{formatStatus(order.status)}
								</Badge>
							</div>
							<div className="flex items-center gap-[1px] mb-4">
								<MapPin className="h-4 w-4 text-[var(--pos-placeholder-color)]"/>
								<span className="font-figtree text-[13px] leading-5 font-regular text-[var(--pos-secondary-text-color)]">
									{(() => {
										const raw = order?.location?.name || order?.locationName;
										if(!raw) return "";
										const formatted = raw
											.toLowerCase()
											.replace(/\b\w/g, (char: string) => char.toUpperCase());
										const details = order?.locationDetails
												? ` ${order.locationDetails}`
												: "";
										return formatted + details;
									})()}
								</span>
							</div>

							{/* Transaction Link */}
							{order.transactionId && (
								<button
									type="button"
									onClick={() => handleTransactionClick(order.transactionId)}
									className="mb-3 flex items-center gap-1.5 text-xs font-medium font-figtree text-[#BABABB] hover:text-[#ffffff] transition-colors"
								>
									<span>Transaction #{order.transactionId}</span>
									<ExternalLink className="h-3 w-3" />
								</button>
							)}

							{/* Transaction Hash */}
							{order.transactionHash && (
								<div className="">
									<div className="flex items-center mb-2">
										<Hash className="h-3 w-3 text-[var(--pos-secondary-text-color)]"/>
										<span className="font-figtree text-[13px] leading-[18px] font-medium text-[var(--pos-secondary-text-color)]">Transaction Hash</span>
									</div>
									<Button
										variant="ghost"
										size="sm"
										className="h-[34px] w-full rounded-sm p-2 flex justify-between items-center bg-[var(--pos-secondary-background-color)]"
										onClick={() => {
											const arbitrumUrl = `https://arbiscan.io/tx/${order.transactionHash}`;
											window.open(arbitrumUrl, '_blank');
										}}
									>
										<span className="font-figtree text-sm leading-[18px] text-[var(--pos-secondary-text-color)] break-all">
											{order.transactionHash.slice(0, 10)}...{order.transactionHash.slice(-8)}
										</span>
										<ExternalLink className="h-3 w-3"/>
									</Button>
								</div>
							)}
						</div>
					</CardContent>
				</Card>

				{/* Order Items Summary */}
				<Card className="w-full mb-2 border-0 p-2 rounded-lg bg-[var(--pos-primary-background-color)]">
					<CardContent className="p-0">
						<h4 className="font-montserrat text-left text-[13px] leading-[18px] font-semibold text-[var(--pos-primary-text-color)] mb-4">Order Items</h4>
						<div className="">
							{order.items?.map((item: any, index: number) => (
								<div key={index} className="flex justify-between items-start border-b border-dashed border-[var(--pos-layout-border-color)] last:border-b-0 mb-3 last:mb-0 pb-3 last:pb-0">
									<div className="flex flex-col">
										<span className="font-figtree text-sm leading-[18px] font-medium text-[var(--pos-secondary-text-color)]">
											{item.product?.name || item.customDrink?.name} x{item.quantity}
										</span>
										{item.modifiers && item.modifiers.length > 0 && (
											<span className="font-figtree text-xs leading-[14px] font-medium text-[var(--pos-secondary-text-color)] mt-1">
												{item.modifiers.map((mod: any, idx: number) => (
													<span key={mod.id}>
														{mod.modifierName}
														{Number(mod.priceAdjustmentUsd) !== 0 && (
															<span className="text-[var(--pos-primary-color)]">
																{Number(mod.priceAdjustmentUsd) > 0 ? ` (+${formatAmount(Number(mod.priceAdjustmentUsd))})` : ` (-${formatAmount(Math.abs(Number(mod.priceAdjustmentUsd)))})`}
															</span>
														)}
														{idx < item.modifiers.length - 1 ? ", " : ""}
													</span>
												))}
											</span>
										)}
									</div>
									<span className="font-figtree text-sm leading-[18px] font-bold text-[var(--pos-primary-text-color)]">{formatAmount(parseFloat(item.unitPriceUsd) * item.quantity)}</span>
								</div>
							))}
						</div>
					</CardContent>
				</Card>

				{/* Bill Summary - matches order detail page exactly */}
				<div className="w-full bg-[var(--pos-primary-background-color)] rounded-lg py-4 px-3 flex flex-col mb-6 text-left">
					<h3 className="mb-4 text-[16px] leading-[18px] font-figtree font-semibold text-[var(--pos-primary-text-color)]">Bill Summary</h3>
					<div className="flex items-center justify-between">
						<span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">Subtotal</span>
						<span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
							{formatAmount(order?.subtotal ? parseFloat(order.subtotal) : originalSubtotal)}
						</span>
					</div>

					{/* Discount Display */}
					{!!(order?.discountAmount && parseFloat(order.discountAmount) > 0) && (
						<div className="flex items-center justify-between mt-2">
							<span className="text-[13px] font-figtree leading-4 font-medium text-[#22C55E]">
								Discount
							</span>
							<span className="text-[13px] font-figtree leading-4 font-medium text-[#22C55E]">
								-{formatAmount(parseFloat(order.discountAmount))}
							</span>
						</div>
					)}
					{/* Legacy discount format */}
					{!order?.discountAmount && order?.discount && (
						<div className="flex items-center justify-between mt-2">
							<span className="text-[13px] font-figtree leading-4 font-medium text-[#22C55E]">
								Discount
							</span>
							<span className="text-[13px] font-figtree leading-4 font-medium text-[#22C55E]">
								-{formatAmount(order.discount.appliedAmount || order.discount.calculatedAmount)}
							</span>
						</div>
					)}

					{/* Tax Breakdown - new format */}
					{Array.isArray(order?.taxBreakdown) && order.taxBreakdown.length > 0 && (
						<>
							{(order.taxBreakdown as Array<{ type: string; rate: number; amount: number; description: string }>).map((fee: any, index: number) => (
								<div key={index} className="flex items-center justify-between mt-2">
									<span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
										Taxes & Fees
									</span>
									<span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
										{formatAmount(fee.amount)}
									</span>
								</div>
							))}
						</>
					)}
					{/* Tax Breakdown - legacy format */}
					{!order?.taxBreakdown && order?.taxCalculation?.fees?.map((fee: any, index: number) => (
						<div key={index} className="flex items-center justify-between mt-2">
							<span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
								Taxes & Fees
							</span>
							<span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
								{formatAmount(fee.amount)}
							</span>
						</div>
					))}

					<div className="border-t border-dashed border-[var(--pos-layout-border-color)] mt-3 pt-3 flex items-center justify-between">
						<span className="text-sm leading-[18px] font-figtree font-bold text-[var(--pos-primary-text-color)]">Total</span>
						<span className="text-sm leading-[18px] font-bold text-[var(--pos-primary-text-color)] font-figtree">
							{isCard
								? `$${order?.totalUsd}`
								: `${parseFloat(order?.totalSech ?? "0").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} SECH`
							}
						</span>
					</div>

					{/* Payment Method */}
					<div className="border-t border-dashed border-[var(--pos-layout-border-color)] mt-3 pt-3 flex items-center justify-between">
						<span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">Payment Method</span>
						{isCard ? (
							<div className="flex items-center gap-1">
								<CardBrandIcon brand={order.cardBrand} width={28} />
								{order.cardLast4 && (
									<span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
										••••{order.cardLast4}
									</span>
								)}
							</div>
						) : (
							<div className="flex items-center gap-1">
								<Wallet className="h-4 w-4 text-[#258750]" />
								{order.walletAddress && (
									<span className="text-[13px] font-figtree leading-4 font-medium text-[#258750]">
										{order.walletAddress.slice(0, 6)}...{order.walletAddress.slice(-4)}
									</span>
								)}
							</div>
						)}
					</div>
				</div>

				{/* Action Buttons */}
				<Button
					onClick={onBackToMenu}
					className="w-full font-figtree bg-[--pos-primary-color] hover:bg-sech-red/90 text-[#ffffff] rounded-lg h-[42px] flex items-center justify-center text-sm leading-[18px] font-semibold"
					size="lg"
				>
					Order More Items
				</Button>
			</div>

			<TransactionDetailDialog
				transaction={selectedTransaction}
				open={selectedTransaction !== null}
				onOpenChange={(open) => { if (!open) setSelectedTransaction(null) }}
			/>
		</div>
	);
}
